import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { agentFlowsApi } from '../../api/agentFlows';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../../components/common/EmptyState';

const K = {
  navy: '#121826',
  blue: '#4185F1',
  border: 'rgba(18, 24, 38, 0.08)',
  surface: '#F8FAFC',
};

const kobraPageSx = {
  p: 3,
  width: '100%',
  maxWidth: { sm: '100%', md: '1700px' },
  bgcolor: K.surface,
};

const kobraFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
    '&:hover fieldset': { borderColor: `${K.blue}55` },
    '&.Mui-focused fieldset': { borderColor: K.blue, borderWidth: 2 },
  },
};

const kobraPrimaryBtnSx = {
  '&&': {
    borderRadius: 1.5,
    px: 3,
    py: 1.25,
    textTransform: 'none',
    fontWeight: 700,
    bgcolor: K.blue,
    color: '#fff',
    border: 'none',
    backgroundImage: 'none',
    boxShadow: `0 4px 14px ${alpha(K.blue, 0.35)}`,
    '&:hover': {
      bgcolor: '#3574d9',
      border: 'none',
      backgroundImage: 'none',
      boxShadow: `0 6px 18px ${alpha(K.blue, 0.4)}`,
    },
  },
};

export function AgentFlowList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const { data: flows = [], isLoading } = useQuery({
    queryKey: ['agentFlows'],
    queryFn: () => agentFlowsApi.listFlows(),
  });

  const createMutation = useMutation({
    mutationFn: () => agentFlowsApi.createFlow({ name: newName }),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['agentFlows'] });
      setCreateOpen(false);
      setNewName('');
      showSuccess('Flujo creado');
      navigate(`/agent-flows/${row.id}/edit`);
    },
    onError: (e) => showError(e?.message || 'No se pudo crear el flujo'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => agentFlowsApi.deleteFlow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentFlows'] });
      showSuccess('Flujo eliminado');
    },
    onError: (e) => showError(e?.message || 'No se pudo eliminar'),
  });

  return (
    <Box sx={kobraPageSx}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: K.navy, mb: 0.5 }}>
            Flujos de agente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Diseña flujos visuales (MVP local). La ejecución en WhatsApp llegará después.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={kobraPrimaryBtnSx}
        >
          Nuevo flujo
        </Button>
      </Box>

      {isLoading ? (
        <Typography color="text.secondary">Cargando…</Typography>
      ) : flows.length === 0 ? (
        <EmptyState
          title="Sin flujos todavía"
          description="Crea un flujo para empezar a conectar nodos en el lienzo."
          actionLabel="Crear flujo"
          onAction={() => setCreateOpen(true)}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {flows.map((f) => (
            <Card
              key={f.id}
              elevation={0}
              sx={{
                border: `1px solid ${K.border}`,
                borderRadius: 2,
                bgcolor: '#fff',
              }}
            >
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: K.navy }}>
                      {f.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Actualizado {f.updatedAt ? new Date(f.updatedAt).toLocaleString() : '—'}
                    </Typography>
                  </Box>
                  <Chip size="small" label={f.status === 'draft' ? 'Borrador' : f.status} variant="outlined" />
                  <IconButton
                    aria-label="Editar"
                    color="primary"
                    onClick={() => navigate(`/agent-flows/${f.id}/edit`)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Eliminar"
                    color="error"
                    onClick={() => {
                      if (window.confirm('¿Eliminar este flujo?')) deleteMutation.mutate(f.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={createOpen} onClose={() => !createMutation.isPending && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nuevo flujo</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Ej. Onboarding ventas"
            sx={{ ...kobraFieldSx, mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={createMutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            sx={kobraPrimaryBtnSx}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
